import { PropsWithChildren, useMemo } from "react";
import { useParams } from "react-router-dom";
import classnames from "classnames";
import { Portal, Tooltip as ArkTooltip } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";
import { usePublishedLocationsQuery } from "@/features/dashboard/features/dashboard";
import { useSelectedTimeRange, useSelectedLocationId } from "@/features/dashboard/store";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import { Icon } from "@/components/icon/icon";
import Popover from "@/components/popover/popover";
import Tool from "@/features/dashboard/components/tool/tool";
import TimeLocationSelector from "@/features/dashboard/components/time-location-selector/time-location-selector";
import { MaritimeWarningTool } from "@/features/dashboard/components/warning-tool/maritime-warning-tool";
import { LandWarningTool } from "@/features/dashboard/components/warning-tool/land-warning-tool";
import { useDashboardData } from "@/features/shared/hooks";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";
import { RefreshButton } from "@/features/dashboard/features/refresh-timer/button";

import styles from "./toolbar.module.css";

interface ToolbarProps {
  dashboardId: string;
  showLocationSelector?: boolean;
  showTimeRangeSelector?: boolean;
}

export default function Toolbar({
  dashboardId,
  showLocationSelector,
  showTimeRangeSelector,
}: ToolbarProps) {
  const { selectedDashboard, navigationElements } = useDashboardData();
  const params = useParams();
  const navLabel = useWarningPageNavLabel();
  // `title` is nullable on built-in warning pages; `navLabel` falls back to the locale default.
  const [title, breadcrumb] = useMemo(() => {
    const currentPage = navigationElements?.find((e) => e.slug === params.page);
    const currentSubPage = currentPage?.children?.find((c) => c.slug === params.subPage);
    if (currentSubPage) return [navLabel(currentSubPage), navLabel(currentPage)];
    if (currentPage) return [navLabel(currentPage), undefined];
    if (selectedDashboard) return [selectedDashboard.name, undefined];
    return ["", undefined];
  }, [selectedDashboard, navigationElements, params.page, params.subPage, navLabel]);

  return (
    <ToolbarRoot>
      <ToolbarTitle title={title} breadcrumb={breadcrumb} />

      <Stack className={styles.tools}>
        <Stack flexWrap="nowrap" alignItems="center" justifyContent="flex-end">
          <RefreshTool />
          <MaritimeWarningTool />
          <LandWarningTool />

          {showLocationSelector && <LocationSelectorTool dashboardId={dashboardId} />}
          {showTimeRangeSelector && <TimeSelectorTool dashboardId={dashboardId} />}

          <TimezoneTool />
        </Stack>
      </Stack>
    </ToolbarRoot>
  );
}

export function ToolbarRoot({ children }: PropsWithChildren) {
  return <div className={styles.toolbar}>{children}</div>;
}

export function ToolbarTitle({
  title,
  breadcrumb,
}: {
  title?: string;
  breadcrumb?: string;
}) {
  return (
    <div className={styles.titleWrapper}>
      {breadcrumb && <div className={styles.breadcrumb}>{breadcrumb}</div>}
      <div
        className={classnames(styles.title, {
          [styles.hasBreadcrumb]: !!breadcrumb,
        })}
      >
        {title}
      </div>
    </div>
  );
}

function RefreshTool() {
  return <RefreshButton />;
}

export function TimezoneTool() {
  const timezone = useWidgetTimezone();

  const tool = (
    <Tool>
      <Icon name="globe" size={16} />
      <span>{timezone?.label}</span>
    </Tool>
  );

  if (!timezone?.tooltip) return tool;

  return (
    <ArkTooltip.Root lazyMount>
      <ArkTooltip.Trigger asChild>{tool}</ArkTooltip.Trigger>
      <Portal>
        <ArkTooltip.Positioner className={styles.tooltip}>
          <ArkTooltip.Content>{timezone.tooltip}</ArkTooltip.Content>
        </ArkTooltip.Positioner>
      </Portal>
    </ArkTooltip.Root>
  );
}

function LocationSelectorTool({ dashboardId }: ToolbarProps) {
  const { t } = useTranslation();
  const locationsQuery = usePublishedLocationsQuery(dashboardId);
  const selectedLocationId = useSelectedLocationId();

  const selectedLocation = locationsQuery.data?.find(
    (location) => location.id === selectedLocationId,
  );

  if (!locationsQuery.isSuccess || locationsQuery.data.length === 0) return null;

  return (
    <Popover
      trigger={(context) => (
        <Button variant="primary">
          <Icon name="locate" size={16} />
          <span>{selectedLocation?.name || t("common.location")}</span>
          <Icon name={context.open ? "chevron-up" : "chevron-down"} size={16} />
        </Button>
      )}
    >
      <TimeLocationSelector
        dashboardId={dashboardId}
        showLocationSelector
        showTimeRangeSelector={false}
      />
    </Popover>
  );
}

export function TimeSelectorTool({ dashboardId }: ToolbarProps) {
  const { t } = useTranslation();
  const { hoursStart, hoursEnd } = useSelectedTimeRange();

  const formattedTimeRange = useMemo(() => {
    if (hoursStart === undefined || hoursEnd === undefined) {
      return;
    }

    const formatHours = (hours: number) => {
      if (hours === 0) return t("common.now");
      const sign = hours > 0 ? "+" : "";
      return `${sign}${hours}h`;
    };

    return `${formatHours(hoursStart)} ${t("common.to")} ${formatHours(hoursEnd)}`;
  }, [hoursStart, hoursEnd, t]);

  return (
    <Popover
      trigger={(context) => (
        <Button variant="primary">
          {formattedTimeRange && (
            <>
              <Icon name="clock" size={16} />
              <span>{formattedTimeRange}</span>
            </>
          )}
          <Icon name={context.open ? "chevron-up" : "chevron-down"} size={16} />
        </Button>
      )}
    >
      <TimeLocationSelector
        dashboardId={dashboardId}
        showLocationSelector={false}
        showTimeRangeSelector
      />
    </Popover>
  );
}
